import { IResponseMessage } from "authentication/domain/IResponseMessage";
import { IMessage } from "domain/IMessage";
import API from "utils/API";
import authHeader from "utils/auth-header";
import { IAccountReceivable } from "../domain/IAccountReceivable";
import { IAccountReceivableCreate } from "../domain/IAccountReceivableCreate";
import { IActivity } from "../domain/IActivity";
import { IMunicipality } from "Dashboard/domain/IMunicipality";
import { IPartner } from "../domain/IPartner";
import { IPayedPayment } from "../domain/IPayedPayment";
import { ICreatePayedPaymentRequestParams } from "../domain/ICreatePayedPaymentRequestParams";
import { IReceivedPayment } from "../domain/IReceivedPayment";
import { IResponseAccountReceivablesWithPayments } from "../domain/IResponseAccountReceivablesWithPayments";
import { IResponseActivites } from "../domain/IResponseActivities";
import { IResponseMunicipalities } from "../domain/IResponseMunicipalities";
import { IResponsePartners } from "../domain/IResponsePartners";
import { IResponsePaymentPayments } from "../domain/IResponsePaidPayments";
import { IResponseReceivedPayments } from "../domain/IResponseReceivedPayments";
import { ICreateReceivedPaymentRequestParams } from "../domain/ICreateReceivedPaymentRequestParams";
import { IResponseAccountPayablesWithPayments } from "../domain/IResponseAccountPayablesWithPayments";
import { IAccountPayable } from "../domain/IAccountPayable";
import { IAccountPayableCreate } from "../domain/IAccountPayableCreate";
import { IPartnerUpdate } from "../domain/IPartnerUpdate";
import { IPartnerCreate } from "../domain/IPartnerCreate";
import { IUpdateAccountReceivableRequestParams } from "../domain/IUpdateAccountReceivableRequestParams";
import { IUpdateAccountPayableRequestParams } from "../domain/IUpdateAccountPayableRequestParams";
import { IAccountReceivablesWithPayments, IGetAllPartnersRequestParams } from "../domain";
import { getQeuryParams } from "utils/queryParamsUtil";
import { IPartnerPlanResponse } from "../domain/IPartnerPlanResponse";
import { IPartnerPlan } from "../domain/IPartnerPlan";
import { IPayablePlanAmount } from "../domain/IPayablePlanAmount";
import { IInvoiceReceivable } from "../domain/IInvoiceReceivable";
import { IInvoiceReceivableParams } from "../domain/IInvoiceReceivableParams";
import { IAlarm } from "../domain/IAlarm";
import { IInvoiceReceivablePrint } from "../domain/IInvoiceReceivablePrint";
import { IUpdateReceivedPaymentRequestParams } from "../domain/IUpdateReceivedPaymentRequestParams";
import { IUpdateReceivablePlanResponse } from "../domain/IUpdateReceivablePlanResponse";
import { IPartnerReceivablesPlan } from "../domain/IPartnerReceivablesPlan";
import { IPartnerReceivablesPlanResponse } from "../domain/IPartnerReceivablesPlanResponse";
import { IResponseAccountReceivablesAdditionalInfo } from "../domain/receivables/IResponseAccountReceivablesAdditionalInfo";
import { IResponseAccountPayablesAdditionalInfo } from "../domain/payables/IResponseAccountPayablesAdditionalInfo";
import { IFinancialCard } from "../domain/payables/IFinancialCard";
import { IAcknowledgeRequestParams } from "../domain/receivables/IAcknowledgeRequestParams";
import { IStockInfoItem } from "../domain/receivables/IStockInfoItem";
import { IUpdatePaidPaymentRequestParams } from "../domain/receivables/IUpdatePaidPaymentRequestParams";
import { IResponsePartnersSimple } from "../domain/IResponsePartnersSimple";

const deleteAccountPayable = async (
  accountPayableId: number
): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/partners/protected/account-payable/${accountPayableId}`,
      {
        headers,
      }
    );
    let message = response.data.message;
    return message;
  } catch (err) {
    throw err;
  }
};

const getAllPayablePlans = async (): Promise<IPartnerPlan[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IPartnerPlanResponse>(
      `/partners/protected/account-payable/plan`,
      { headers }
    );
    let partnerPlans = response.data.partnerPlans;
    return partnerPlans;
  } catch (err) {
    throw err;
  }
};

const getAllPaidPayments = async (
  accountPayableId: number
): Promise<IPayedPayment[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponsePaymentPayments>(
      `/partners/protected/paid-payment?accountPayableId=${accountPayableId}`,
      { headers }
    );
    let paymentPayments = response.data.paymentPayments;
    return paymentPayments;
  } catch (err) {
    throw err;
  }
};

const createPaidPayment = async (
  payment: ICreatePayedPaymentRequestParams
): Promise<IPayedPayment> => {
  var headers = authHeader();
  try {
    const response = await API.post<IPayedPayment>(
      "/partners/protected/paid-payment",
      payment,
      { headers }
    );
    let paymentPayment = response.data;
    return paymentPayment;
  } catch (err) {
    throw err;
  }
};

const updatePaidPayment = async (
  payment: IUpdatePaidPaymentRequestParams
): Promise<IPayedPayment> => {
  var headers = authHeader();
  try {
    const { paidPaymentId, ...rest } = payment;

    const response = await API.put<IPayedPayment>(
      `/partners/protected/paid-payment/${paidPaymentId}`,
      { ...rest },
      { headers }
    );
    let paidPayment = response.data;
    return paidPayment;
  } catch (err) {
    throw err;
  }
};

const deletePaidPayment = async (paidPaymentId: number): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/partners/protected/paid-payment/${paidPaymentId}`,
      { headers }
    );
    let message = response.data.message;
    return message;
  } catch (err) {
    throw err;
  }
};

const createPartner = async (partnerCreate: IPartnerCreate): Promise<IPartner> => {
  var headers = authHeader();
  try {
    const response = await API.post<IPartner>(
      "/partners/protected/partner",
      partnerCreate,
      { headers }
    );
    let partner = response.data;
    return partner;
  } catch (err) {
    throw err;
  }
};

const getAllPartners = async (
  params?: IGetAllPartnersRequestParams
): Promise<IResponsePartners> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponsePartners>(
      `/partners/public/partner${getQeuryParams(params)}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getPartnersForDropdown = async (): Promise<IResponsePartnersSimple> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponsePartners>(
      "/partners/public/partner-for-dropdown",
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};


const updatePartner = async (partner: IPartnerUpdate): Promise<IPartner> => {
  var headers = authHeader();
  const { partnerId, ...rest } = partner;
  try {
    const response = await API.put(
      `/partners/protected/partner/${partner.partnerId}`,
      { ...rest },
      { headers }
    );
    let upartner = response.data;
    return upartner;
  } catch (err) {
    throw err;
  }
};

const updatePayablePlan = async (
  accountPayableId: number,
  planned: boolean
): Promise<IPayablePlanAmount> => {
  var headers = authHeader();
  try {
    const response = await API.put<IPayablePlanAmount>(
      `/partners/protected/account-payable/${accountPayableId}/plan`,
      { planned: planned },
      { headers }
    );
    let plan = response.data;
    return plan;
  } catch (err) {
    throw err;
  }
};

const resetPayablePlans = async (): Promise<IPayablePlanAmount> => {
  var headers = authHeader();
  try {
    const response = await API.put<IPayablePlanAmount>(
      "/partners/protected/account-payable/reset-payable-plans",
      {},
      { headers }
    );
    let plan = response.data;
    return plan;
  } catch (err) {
    throw err;
  }
};

const getAllReceivablePlans = async (): Promise<IPartnerReceivablesPlan[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IPartnerReceivablesPlanResponse>(
      `/partners/protected/account-receivable/plan`,
      { headers }
    );
    let partnerPlans = response.data.partnerPlans;
    return partnerPlans;
  } catch (err) {
    throw err;
  }
};

const updateReceivablePlan = async (
  accountReceivableId: number,
  planned: boolean
): Promise<IUpdateReceivablePlanResponse> => {
  var headers = authHeader();
  try {
    const response = await API.put<IUpdateReceivablePlanResponse>(
      `/partners/protected/account-receivable/${accountReceivableId}/plan`,
      { planned: planned },
      { headers }
    );
    let plan = response.data;
    return plan;
  } catch (err) {
    throw err;
  }
};

const resetReceivablePlans = async (): Promise<IUpdateReceivablePlanResponse> => {
  var headers = authHeader();
  try {
    const response = await API.put<IUpdateReceivablePlanResponse>(
      "/partners/protected/account-receivable/reset-receivable-plans",
      {},
      { headers }
    );
    let plan = response.data;
    return plan;
  } catch (err) {
    throw err;
  }
};

const deletePartner = async (partnerId: number): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/partners/protected/partner/${partnerId}`,
      { headers }
    );
    let message = response.data.message;
    return message;
  } catch (err) {
    throw err;
  }
};

const getAllMunicipalities = async (): Promise<IMunicipality[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseMunicipalities>(
      "/common/public/municipality",
      { headers }
    );
    let municipalities = response.data.municipalities;
    return municipalities;
  } catch (err) {
    throw err;
  }
};

const createMunicipality = async (name: string): Promise<IMunicipality> => {
  var headers = authHeader();
  try {
    const response = await API.post<IMunicipality>(
      "/common/protected/municipality",
      { name },
      { headers }
    );
    let municipality = response.data;
    return municipality;
  } catch (err) {
    throw err;
  }
};

const updateMunicipality = async (
  municipality: IMunicipality
): Promise<IMunicipality> => {
  var headers = authHeader();
  try {
    const response = await API.put<IMunicipality>(
      `/common/protected/municipality/${municipality.municipalityId}`,
      { name: municipality.name },
      { headers }
    );
    let umunicipality = response.data;
    return umunicipality;
  } catch (err) {
    throw err;
  }
};

const deleteMunicipality = async (municipalityId: number): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/common/protected/municipality/${municipalityId}`,
      { headers }
    );
    let message = response.data.message;
    return message;
  } catch (err) {
    throw err;
  }
};

const getAllActivities = async (): Promise<IActivity[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseActivites>(
      `/partners/public/activity`,
      { headers }
    );
    let activities = response.data.activities;
    return activities;
  } catch (err) {
    throw err;
  }
};

const createActivity = async (name: string): Promise<IActivity> => {
  var headers = authHeader();
  try {
    const response = await API.post<IActivity>(
      `/partners/protected/activity`,
      { name },
      { headers }
    );
    let activity = response.data;
    return activity;
  } catch (err) {
    throw err;
  }
};

const updateActivity = async (activity: IActivity): Promise<IActivity> => {
  var headers = authHeader();
  try {
    const response = await API.put<IActivity>(
      `/partners/protected/activity/${activity.name}`,
      { name: activity.name },
      { headers }
    );
    let uactivity = response.data;
    return uactivity;
  } catch (err) {
    throw err;
  }
};

const deleteActivity = async (activityId: number): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/partners/protected/activity/${activityId}`,
      { headers }
    );
    let message = response.data.message;
    return message;
  } catch (err) {
    throw err;
  }
};

const getAllAccountReceivables = async (
  partnerId: number
): Promise<IResponseAccountReceivablesWithPayments> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseAccountReceivablesWithPayments>(
      `/partners/protected/account-receivable?partnerId=${partnerId}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const createAccountReceivable = async (
  data: IAccountReceivableCreate
): Promise<IAccountReceivablesWithPayments> => {
  var headers = authHeader();
  try {
    const response = await API.post<IAccountReceivablesWithPayments>(
      "/partners/protected/account-receivable",
      data,
      { headers }
    );
    let accountReceivable = response.data;
    return accountReceivable;
  } catch (err) {
    throw err;
  }
};

const getReceivableInvoice = async (
  accountReceivableId: number
): Promise<IInvoiceReceivable> => {
  var headers = authHeader();
  try {
    const response = await API.get(
      `/partners/protected/account-receivable/${accountReceivableId}/invoice`,
      { headers }
    );
    const invoice: IInvoiceReceivable = response.data;
    if (
      invoice.items.length < 1 ||
      (invoice.items[invoice.items.length - 1].title &&
        invoice.items[invoice.items.length - 1].title !== "") ||
      (invoice.items[invoice.items.length - 1].quantity &&
        invoice.items[invoice.items.length - 1].quantity !== 0) ||
      (invoice.items[invoice.items.length - 1].unitPrice &&
        invoice.items[invoice.items.length - 1].unitPrice !== 0)
    ) {
      invoice.items.push({});
    }
    return invoice;
  } catch (err) {
    throw err;
  }
};

const getReceivableInvoiceForPrint = async (
  accountReceivableId: number
): Promise<IInvoiceReceivablePrint> => {
  var headers = authHeader();
  try {
    const response = await API.get(
      `/partners/protected/account-receivable/${accountReceivableId}/invoice/print`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const updateReceivableInvoice = async (
  accountReceivableId: number,
  invoiceInfo: IInvoiceReceivableParams
) => {
  var headers = authHeader();
  try {
    const response = await API.post(
      `/partners/protected/account-receivable/${accountReceivableId}/invoice`,
      invoiceInfo,
      { headers }
    );
    const invoice: IInvoiceReceivable = response.data;
    if (
      invoice.items.length < 1 ||
      (invoice.items[invoice.items.length - 1].title &&
        invoice.items[invoice.items.length - 1].title !== "") ||
      (invoice.items[invoice.items.length - 1].quantity &&
        invoice.items[invoice.items.length - 1].quantity !== 0) ||
      (invoice.items[invoice.items.length - 1].unitPrice &&
        invoice.items[invoice.items.length - 1].unitPrice !== 0)
    ) {
      invoice.items.push({});
    }
    return invoice;
  } catch (err) {
    throw err;
  }
};

const deleteAccountReceivable = async (
  accountReceivableId: number
): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IResponseMessage>(
      `/partners/protected/account-receivable/${accountReceivableId}`,
      { headers }
    );
    let message = response.data.message;
    return message;
  } catch (err) {
    throw err;
  }
};

const getAllReceivedPayments = async (
  accountReceivableId: number
): Promise<IReceivedPayment[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseReceivedPayments>(
      `/partners/protected/received-payment?accountReceivableId=${accountReceivableId}`,
      { headers }
    );
    let receivedPayments = response.data.receivedPayments;
    return receivedPayments;
  } catch (err) {
    throw err;
  }
};

const createReceivedPayment = async (
  payment: ICreateReceivedPaymentRequestParams
): Promise<IReceivedPayment> => {
  var headers = authHeader();
  try {
    const response = await API.post<IReceivedPayment>(
      "/partners/protected/received-payment",
      payment,
      { headers }
    );
    let receivedPayment = response.data;
    return receivedPayment;
  } catch (err) {
    throw err;
  }
};

const updateReceivedPayment = async (
  payment: IUpdateReceivedPaymentRequestParams
): Promise<IReceivedPayment> => {
  var headers = authHeader();
  try {
    const { receivedPaymentId, ...rest } = payment;

    const response = await API.put<IReceivedPayment>(
      `/partners/protected/received-payment/${receivedPaymentId}`,
      { ...rest },
      { headers }
    );
    let receivedPayment = response.data;
    return receivedPayment;
  } catch (err) {
    throw err;
  }
};

const deleteReceivedPayment = async (
  receivedPaymentId: number
): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/partners/protected/received-payment/${receivedPaymentId}`,
      {
        headers,
      }
    );
    let message = response.data.message;
    return message;
  } catch (err) {
    throw err;
  }
};

const getReceivablesAdditionalInfo = async (partnerId: number): Promise<IResponseAccountReceivablesAdditionalInfo> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseAccountReceivablesAdditionalInfo>(
      `/partners/protected/account-receivable-additional-info?partnerId=${partnerId}`,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
}

const updateReceivablesAdditionalInfoForStock = async (data: IAcknowledgeRequestParams): Promise<IStockInfoItem> => {
  var headers = authHeader();
  try {
    const response = await API.put<IStockInfoItem>(
      "/partners/protected/account-receivable-additional-info/acknowledge-stock",
      { ...data },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
}

const updateReceivablesAdditionalInfoForConcrete = async (data: IAcknowledgeRequestParams): Promise<IStockInfoItem> => {
  var headers = authHeader();
  try {
    const response = await API.put<IStockInfoItem>(
      "/partners/protected/account-receivable-additional-info/acknowledge-concrete",
      { ...data },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
}

const getAllAccountPayables = async (
  partnerId: number
): Promise<IResponseAccountPayablesWithPayments> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseAccountPayablesWithPayments>(
      `/partners/protected/account-payable?partnerId=${partnerId}`,
      { headers }
    );

    return response.data;
  } catch (err) {
    throw err;
  }
};

const createAccountPayable = async (
  data: IAccountPayableCreate
): Promise<IAccountPayable> => {
  var headers = authHeader();
  try {
    const response = await API.post<IAccountPayable>(
      "/partners/protected/account-payable",
      data,
      { headers }
    );
    let accountPayable = response.data;
    return accountPayable;
  } catch (err) {
    throw err;
  }
};

const updateAccountReceivable = async (
  params: IUpdateAccountReceivableRequestParams
): Promise<IAccountReceivable> => {
  var headers = authHeader();
  try {
    const { accountReceivableId, ...rest } = params;
    const response = await API.put<IAccountReceivable>(
      `/partners/protected/account-receivable/${accountReceivableId}`,
      { ...rest },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const updateAccountPayableAlarm = async (
  date: string,
  note: string,
  payableId: number
) => {
  var headers = authHeader();
  try {
    const response = await API.put<IAlarm>(
      `partners/protected/account-payable/${payableId}/alarm`,
      { date: date, note: note },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const updateAccountPayable = async (
  params: IUpdateAccountPayableRequestParams
): Promise<IAccountPayable> => {
  var headers = authHeader();
  try {
    const { accountPayableId, ...rest } = params;
    const response = await API.put<IAccountPayable>(
      `/partners/protected/account-payable/${accountPayableId}`,
      { ...rest },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getPayablesAdditionalInfo = async (partnerId: number): Promise<IResponseAccountPayablesAdditionalInfo> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseAccountPayablesAdditionalInfo>(
      `/partners/protected/account-payable-additional-info?partnerId=${partnerId}`,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
}

const getPayablesFinancialCard = async (partnerId: number): Promise<IFinancialCard> => {
  var headers = authHeader();
  try {
    const response = await API.get<IFinancialCard>(
      `/partners/protected/account-payable-financial-card?partnerId=${partnerId}`,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
}

export const partnersService = {
  createPartner,
  getAllPartners,
  getPartnersForDropdown,
  updatePartner,
  deletePartner,
  getAllMunicipalities,
  createMunicipality,
  updateMunicipality,
  deleteMunicipality,
  getAllActivities,
  createActivity,
  updateActivity,
  deleteActivity,
  getAllAccountReceivables,
  createAccountReceivable,
  updateAccountReceivable,
  deleteAccountReceivable,
  getAllReceivedPayments,
  createReceivedPayment,
  updateReceivedPayment,
  deleteReceivedPayment,
  getReceivablesAdditionalInfo,
  updateReceivablesAdditionalInfoForStock,
  updateReceivablesAdditionalInfoForConcrete,
  getAllAccountPayables,
  createAccountPayable,
  updateAccountPayable,
  deleteAccountPayable,
  getAllPaidPayments,
  createPaidPayment,
  updatePaidPayment,
  deletePaidPayment,
  getPayablesAdditionalInfo,
  getAllPayablePlans,
  updatePayablePlan,
  resetPayablePlans,
  getAllReceivablePlans,
  updateReceivablePlan,
  resetReceivablePlans,
  getReceivableInvoice,
  updateReceivableInvoice,
  updateAccountPayableAlarm,
  getReceivableInvoiceForPrint,
  getPayablesFinancialCard,
};
